import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '@/api/client'
import { Spinner } from '@/components/ui/Spinner'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import { useLocalizedTeamName } from '@/utils/teamNames'

interface StandingRow {
  position: number
  tla: string
  name: string
  played: number
  won: number
  draw: number
  lost: number
  points: number
  gd: number
  gf: number
  ga: number
  flag_emoji: string | null
}

interface StandingsGroup {
  group: string
  table: StandingRow[]
}

interface StandingsResponse {
  standings: StandingsGroup[]
  fetchedAt: number | null
}

const REFRESH_INTERVAL = 60_000

export function StandingsPage() {
  const { t } = useTranslation()
  const getTeamName = useLocalizedTeamName()
  const [data, setData] = useState<StandingsGroup[]>([])
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL / 1000)

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<StandingsResponse>('/standings')
      setData(res.standings)
      if (res.fetchedAt) setFetchedAt(new Date(res.fetchedAt))
    } finally {
      setLoading(false)
      setCountdown(REFRESH_INTERVAL / 1000)
    }
  }, [])

  // Auto-refresh
  useEffect(() => {
    load()
    const refresh = setInterval(load, REFRESH_INTERVAL)
    return () => clearInterval(refresh)
  }, [load])

  // Countdown ticker
  useEffect(() => {
    const tick = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000)
    return () => clearInterval(tick)
  }, [])

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h1 className="text-base font-black text-white leading-tight">{t('standings.title')}</h1>
              <p className="text-[11px] text-white/40">
                {fetchedAt
                  ? t('standings.fetchedAt', { time: fetchedAt.toLocaleTimeString() })
                  : t('leaderboard.loading')}
                {' · '}
                <span className="tabular-nums">{countdown}s</span>
              </p>
            </div>
          </div>
          <nav className="flex gap-2 items-center">
            <LangSwitcher />
            <a href="/bracket" className="btn-secondary btn-sm text-sm">⚽ {t('nav.bracket')}</a>
            <a href="/leaderboard" className="btn-secondary btn-sm text-sm">🏅 {t('nav.leaderboard')}</a>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-32"><Spinner size="lg" /></div>
        ) : data.length === 0 ? (
          <div className="text-center py-32 text-white/40">
            <div className="text-5xl mb-4">📋</div>
            <p className="text-lg font-medium">{t('standings.noData')}</p>
            <p className="text-sm mt-2">{t('standings.noDataDesc')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.map(g => (
              <GroupCard key={g.group} group={g} getTeamName={getTeamName} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function GroupCard({
  group,
  getTeamName,
  t,
}: {
  group: StandingsGroup
  getTeamName: (id: string | null | undefined, name: string | null | undefined) => string
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <div className="card overflow-hidden">
      {/* Group header */}
      <div className="px-4 py-2.5 border-b border-white/10 bg-white/[0.03] flex items-center justify-between">
        <span className="text-sm font-black text-white tracking-wide">
          {t('standings.group', { name: group.group })}
        </span>
        <div className="flex gap-2 text-[10px] font-bold text-white/30 uppercase tracking-wider">
          <span className="w-5 text-center">{t('standings.w')}</span>
          <span className="w-5 text-center">{t('standings.d')}</span>
          <span className="w-5 text-center">{t('standings.l')}</span>
          <span className="w-8 text-center">{t('standings.gd')}</span>
          <span className="w-7 text-center">{t('standings.pts')}</span>
        </div>
      </div>

      {/* Table rows */}
      <div>
        {group.table.map((row, idx) => {
          const advances = idx < 2
          const bestThird = idx === 2

          return (
            <div
              key={row.tla}
              className={`flex items-center gap-2 px-3 py-2 border-b border-white/5 last:border-0 transition-colors
                ${advances ? 'bg-emerald-500/5' : bestThird ? 'bg-amber-500/3' : ''}`}
            >
              {/* Position */}
              <span className={`text-xs font-bold w-4 text-center shrink-0
                ${advances ? 'text-emerald-400' : bestThird ? 'text-amber-400' : 'text-white/30'}`}>
                {row.position}
              </span>

              {/* Flag + Name */}
              <span className="text-base shrink-0">{row.flag_emoji ?? '🏳'}</span>
              <span className={`text-sm font-medium flex-1 min-w-0 truncate
                ${advances ? 'text-white' : 'text-white/70'}`}>
                {getTeamName(row.tla, row.name)}
              </span>

              {/* Stats */}
              <div className="flex gap-2 text-xs tabular-nums shrink-0">
                <span className="w-5 text-center text-white/50">{row.won}</span>
                <span className="w-5 text-center text-white/50">{row.draw}</span>
                <span className="w-5 text-center text-white/50">{row.lost}</span>
                <span className={`w-8 text-center font-medium ${row.gd > 0 ? 'text-emerald-400' : row.gd < 0 ? 'text-red-400' : 'text-white/40'}`}>
                  {row.gd > 0 ? `+${row.gd}` : row.gd}
                </span>
                <span className={`w-7 text-center font-black ${advances ? 'text-emerald-400' : 'text-white/80'}`}>
                  {row.points}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend strip */}
      <div className="px-3 py-1.5 flex gap-3 text-[10px] text-white/25">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-emerald-500/40 inline-block" />
          {t('standings.qualifies')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-amber-500/30 inline-block" />
          {t('standings.thirdPlayoff')}
        </span>
      </div>
    </div>
  )
}
